/**
 * Commodity Pending Detail Panel (KAN-307 Phase 4)
 *
 * Side panel for confirming or rejecting pending modification/cancellation requests.
 * Renders inline within the Pending tab — no separate dialog required.
 *
 * For modify requests: shows trade summary, initiator info, editable fields
 * table (Field | Current | New), and Confirm/Reject buttons.
 *
 * For cancel requests: shows trade summary, initiator info, cancellation
 * reason, confirmation checkbox, and Confirm/Reject buttons.
 *
 * Reject mode: inline reason textarea with Confirm Reject button.
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, AlertTriangle, CheckCircle2, Clock } from 'lucide-react';
import { commodityModificationService } from '../../../services/api/commodityModificationService';
import { clientService } from '../../../services/api/clientService';

// ── Constants & helpers duplicated from CommodityModifyDialog ──

const MONTH_CODES: { letter: string; name: string; month: number }[] = [
  { letter: 'F', name: 'Jan', month: 1 },
  { letter: 'G', name: 'Feb', month: 2 },
  { letter: 'H', name: 'Mar', month: 3 },
  { letter: 'J', name: 'Apr', month: 4 },
  { letter: 'K', name: 'May', month: 5 },
  { letter: 'M', name: 'Jun', month: 6 },
  { letter: 'N', name: 'Jul', month: 7 },
  { letter: 'Q', name: 'Aug', month: 8 },
  { letter: 'U', name: 'Sep', month: 9 },
  { letter: 'V', name: 'Oct', month: 10 },
  { letter: 'X', name: 'Nov', month: 11 },
  { letter: 'Z', name: 'Dec', month: 12 },
];

function parseFixingRef(ref: string): { prefix: string; monthLetter: string; year2d: string } | null {
  if (!ref || ref.length < 3) return null;
  const year2d = ref.slice(-2);
  const monthLetter = ref.charAt(ref.length - 3);
  const prefix = ref.slice(0, -3);
  if (!/^\d{2}$/.test(year2d)) return null;
  if (!MONTH_CODES.find(m => m.letter === monthLetter)) return null;
  return { prefix, monthLetter, year2d };
}

function generateFixingRefOptions(currentRef: string): string[] {
  const parsed = parseFixingRef(currentRef);
  if (!parsed) return [currentRef];
  const { prefix } = parsed;
  const now = new Date();
  const options: string[] = [];
  for (let offset = -24; offset <= 24; offset++) {
    const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    const y2d = String(d.getFullYear()).slice(-2);
    const mc = MONTH_CODES[d.getMonth()];
    options.push(`${prefix}${mc.letter}${y2d}`);
  }
  return options;
}

type FieldType = 'readonly' | 'display' | 'text' | 'number' | 'date' | 'select' | 'fixingRef' | 'counterparty';

interface FieldConfig {
  key: string;
  type: FieldType;
  options?: string[];
  optionOnly?: boolean;
  optional?: boolean;
}

const DIALOG_FIELDS: FieldConfig[] = [
  { key: 'ProductType', type: 'display' },
  { key: 'UnderlyingName', type: 'display' },
  { key: 'OurParty', type: 'display' },
  { key: 'LegacyReference1', type: 'display' },
  { key: 'CounterpartyName', type: 'counterparty' },
  { key: 'Direction', type: 'select', options: ['Buy', 'Sell'] },
  { key: 'TradeDate', type: 'date' },
  { key: 'ValueDate', type: 'date' },
  { key: 'Lots', type: 'number' },
  { key: 'Price', type: 'number' },
  { key: 'Quantity', type: 'readonly' },
  { key: 'OptionType', type: 'select', options: ['Put', 'Call'], optionOnly: true },
  { key: 'MaturityDate', type: 'date' },
  { key: 'FixingReference', type: 'fixingRef' },
  { key: 'PaymentDate', type: 'date' },
  { key: 'Grouping1', type: 'text', optional: true },
  { key: 'Grouping2', type: 'text', optional: true },
  { key: 'Grouping3', type: 'text', optional: true },
];

function parseLocaleNumber(input: string): number {
  if (!input || !input.trim()) return NaN;
  let s = input.trim();
  const lastDot = s.lastIndexOf('.');
  const lastComma = s.lastIndexOf(',');
  if (lastComma > lastDot) {
    s = s.replace(/\./g, '').replace(',', '.');
  } else if (lastDot > lastComma) {
    s = s.replace(/,/g, '');
  } else {
    s = s.replace(/,/g, '');
  }
  return Number(s);
}

// ── Component ──

interface Props {
  trade: any;
  clientId: string;
  canConfirm: boolean;
  userTimezone: string;
  onConfirmSuccess: (result: any) => void;
  onRejectSuccess: (result: any) => void;
  onClose: () => void;
}

const CommodityPendingDetailPanel: React.FC<Props> = ({
  trade,
  clientId,
  canConfirm,
  userTimezone,
  onConfirmSuccess,
  onRejectSuccess,
  onClose,
}) => {
  const { t, i18n } = useTranslation();

  const pendingRequest = trade.pendingRequest || {};
  const action: 'modify' | 'cancel' = pendingRequest.action || 'modify';
  const isModify = action === 'modify';
  const isCancel = action === 'cancel';
  const isOption = trade.ProductType === 'Option';
  const entityId = trade.entityId || clientId;

  // ── Modify form state ──
  const editableFields = useMemo(
    () => DIALOG_FIELDS.filter(f => f.type !== 'display' && f.type !== 'readonly'),
    []
  );

  const [formValues, setFormValues] = useState<Record<string, any>>(() => {
    const initial: Record<string, any> = {};
    for (const f of editableFields) {
      initial[f.key] = trade[f.key] ?? '';
    }
    return initial;
  });

  const [fieldErrors, setFieldErrors] = useState<Set<string>>(new Set());
  const tableRef = useRef<HTMLTableElement>(null);

  // ── Cancel form state ──
  const [cancellationReason, setCancellationReason] = useState('');
  const [cancelConfirmed, setCancelConfirmed] = useState(false);

  // ── Reject mode state ──
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  // ── Common state ──
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Counterparty dropdown options
  const [counterpartyOptions, setCounterpartyOptions] = useState<{ counterpartyName: string; bankId: string }[]>([]);

  useEffect(() => {
    if (isModify) {
      clientService.getCounterpartyNames(clientId, 'commodity').then(res => {
        if (res.success && res.data) setCounterpartyOptions(res.data);
      }).catch(() => {});
    }
  }, [clientId, isModify]);

  // Reset state when trade changes
  useEffect(() => {
    const initial: Record<string, any> = {};
    for (const f of editableFields) {
      initial[f.key] = trade[f.key] ?? '';
    }
    setFormValues(initial);
    setFieldErrors(new Set());
    setCancellationReason('');
    setCancelConfirmed(false);
    setRejectMode(false);
    setRejectReason('');
    setError(null);
  }, [trade.id, editableFields]);

  // ── Quantity calculation (same as CommodityModifyDialog) ──
  const hasUnderlyingFactors = trade._quantityMode !== undefined;
  const quantityMode = trade._quantityMode || 'formula';
  const quantityConversionFactor = Number(trade._quantityConversionFactor) || 1;
  const lotSizeMultiplier = Number(trade._lotSizeMultiplier) || 1;
  const quantityMultiplier = Number(trade._quantityMultiplier) || 1;

  const derivedFactor = useMemo(() => {
    const origPrice = Number(trade.Price);
    const origLots = Number(trade.Lots);
    const origQty = Number(trade.Quantity);
    if (!origPrice || !origLots || !origQty) return null;
    return origQty / (origPrice * origLots);
  }, [trade.Price, trade.Lots, trade.Quantity]);

  const fixingRefOptions = useMemo(
    () => generateFixingRefOptions(trade.FixingReference || ''),
    [trade.FixingReference]
  );

  const previewQuantity = useMemo(() => {
    if (quantityMode === 'direct') return null;
    const curPrice = parseLocaleNumber(String(formValues.Price ?? trade.Price));
    const curLots = parseLocaleNumber(String(formValues.Lots ?? trade.Lots));
    if (isNaN(curPrice) || isNaN(curLots) || !curPrice || !curLots) return null;
    if (hasUnderlyingFactors) {
      return curPrice * quantityConversionFactor * curLots * lotSizeMultiplier * quantityMultiplier;
    }
    if (derivedFactor == null) return null;
    return curPrice * curLots * derivedFactor;
  }, [quantityMode, hasUnderlyingFactors, quantityConversionFactor, lotSizeMultiplier, quantityMultiplier, derivedFactor, formValues.Price, formValues.Lots, trade.Price, trade.Lots]);

  // ── Changed fields derivation ──
  const changedFields = useMemo(() => {
    const changed: Record<string, any> = {};
    for (const f of editableFields) {
      const original = trade[f.key] ?? '';
      const current = formValues[f.key] ?? '';
      if (f.type === 'number') {
        const origNum = original === '' ? '' : Number(original);
        const curNum = current === '' ? '' : parseLocaleNumber(String(current));
        if (origNum !== curNum && !isNaN(curNum as number)) changed[f.key] = curNum;
      } else {
        if (String(original) !== String(current)) changed[f.key] = current;
      }
    }
    return changed;
  }, [formValues, trade, editableFields]);

  const hasChanges = Object.keys(changedFields).length > 0;
  const priceOrLotsChanged = 'Price' in changedFields || 'Lots' in changedFields;

  const maturityMonthChanged = useMemo(() => {
    if (!('MaturityDate' in changedFields)) return false;
    const origDate = trade.MaturityDate || '';
    const newDate = String(changedFields.MaturityDate || '');
    if (!origDate || !newDate) return false;
    return origDate.substring(0, 7) !== newDate.substring(0, 7);
  }, [changedFields, trade.MaturityDate]);

  // ── Formatting helpers ──
  const fieldLabel = useCallback((key: string) => {
    return t(`commodities.fields.${key.charAt(0).toLowerCase() + key.slice(1)}`, key);
  }, [t]);

  const formatDate = useCallback((value: any): string => {
    if (!value) return '—';
    const s = String(value).substring(0, 10);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
  }, []);

  const formatNumber = useCallback((value: any): string => {
    if (value == null || value === '') return '—';
    const num = Number(value);
    if (isNaN(num)) return String(value);
    return num.toLocaleString(i18n.language, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  }, [i18n.language]);

  const formatValue = useCallback((key: string, value: any) => {
    if (value == null || value === '') return '—';
    const fieldDef = DIALOG_FIELDS.find(f => f.key === key);
    if (fieldDef?.type === 'date') return formatDate(value);
    if (fieldDef?.type === 'number' || fieldDef?.type === 'readonly') return formatNumber(value);
    return String(value);
  }, [formatDate, formatNumber]);

  const formatTimestamp = useCallback((value: any): string => {
    if (!value) return '—';
    const d = new Date(String(value));
    if (isNaN(d.getTime())) return String(value);
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: userTimezone,
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    }).formatToParts(d);
    const get = (type: string) => parts.find(p => p.type === type)?.value || '';
    return `${get('day')}/${get('month')}/${get('year')} ${get('hour')}:${get('minute')}:${get('second')}`;
  }, [userTimezone]);

  // ── Field change handler ──
  const handleFieldChange = useCallback((key: string, value: string) => {
    setFormValues(prev => ({ ...prev, [key]: value }));
    setFieldErrors(prev => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);

  // ── Confirm handler ──
  const handleConfirm = useCallback(async () => {
    setError(null);

    if (isModify) {
      if (!hasChanges) return;

      // Validate all editable fields
      const errors = new Set<string>();
      for (const f of editableFields) {
        if (f.optional) continue;
        if (f.optionOnly && !isOption) continue;
        const val = formValues[f.key] ?? '';
        if (String(val).trim() === '') {
          errors.add(f.key);
        } else if (f.type === 'number') {
          const num = parseLocaleNumber(String(val));
          if (isNaN(num) || num <= 0) errors.add(f.key);
        }
      }
      if (errors.size > 0) {
        setFieldErrors(errors);
        const firstKey = editableFields.find(f => errors.has(f.key))?.key;
        if (firstKey && tableRef.current) {
          const row = tableRef.current.querySelector(`[data-field="${firstKey}"]`);
          row?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }
      setFieldErrors(new Set());

      // Parse numeric fields
      const parsedChanges: Record<string, any> = {};
      for (const [key, val] of Object.entries(changedFields)) {
        const fieldDef = editableFields.find(f => f.key === key);
        if (fieldDef?.type === 'number') {
          parsedChanges[key] = parseLocaleNumber(String(val));
        } else {
          parsedChanges[key] = val;
        }
      }

      setSubmitting(true);
      try {
        const res = await commodityModificationService.confirm(
          clientId,
          trade.id,
          { modifiedFields: parsedChanges },
          entityId
        );
        if (res.success) {
          onConfirmSuccess(res.data);
        } else {
          setError(res.message || t('commodities.modifications.error'));
        }
      } catch (err: any) {
        setError(err.message || t('commodities.modifications.error'));
      } finally {
        setSubmitting(false);
      }
    } else {
      // Cancel confirmation
      if (!cancellationReason.trim()) {
        setError(t('commodities.modifications.cancellationReasonRequired'));
        return;
      }
      if (!cancelConfirmed) return;

      setSubmitting(true);
      try {
        const res = await commodityModificationService.confirm(
          clientId,
          trade.id,
          { cancellationReason: cancellationReason.trim() },
          entityId
        );
        if (res.success) {
          onConfirmSuccess(res.data);
        } else {
          setError(res.message || t('commodities.modifications.error'));
        }
      } catch (err: any) {
        setError(err.message || t('commodities.modifications.error'));
      } finally {
        setSubmitting(false);
      }
    }
  }, [isModify, isOption, hasChanges, changedFields, editableFields, formValues, cancellationReason, cancelConfirmed, clientId, trade.id, entityId, onConfirmSuccess, t]);

  // ── Reject handler ──
  const handleReject = useCallback(async () => {
    if (!rejectReason.trim()) {
      setError(t('commodities.modifications.rejectReasonRequired'));
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const res = await commodityModificationService.reject(
        clientId,
        trade.id,
        { reason: rejectReason.trim() },
        entityId
      );
      if (res.success) {
        onRejectSuccess(res.data);
      } else {
        setError(res.message || t('commodities.modifications.error'));
      }
    } catch (err: any) {
      setError(err.message || t('commodities.modifications.error'));
    } finally {
      setSubmitting(false);
    }
  }, [rejectReason, clientId, trade.id, entityId, onRejectSuccess, t]);

  // ── Render field input (same as CommodityModifyDialog) ──
  const renderFieldInput = (f: FieldConfig) => {
    switch (f.type) {
      case 'select':
        return (
          <select
            value={formValues[f.key] || ''}
            onChange={(e) => handleFieldChange(f.key, e.target.value)}
            disabled={!canConfirm}
          >
            {f.options?.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );
      case 'fixingRef':
        return (
          <select
            value={formValues[f.key] || ''}
            onChange={(e) => handleFieldChange(f.key, e.target.value)}
            disabled={!canConfirm}
          >
            {fixingRefOptions.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );
      case 'counterparty':
        if (counterpartyOptions.length > 0) {
          return (
            <select
              value={formValues[f.key] || ''}
              onChange={(e) => handleFieldChange(f.key, e.target.value)}
              disabled={!canConfirm}
            >
              <option value="">{t('common.select')}</option>
              {counterpartyOptions.map((cp) => (
                <option key={cp.bankId} value={cp.counterpartyName}>{cp.counterpartyName}</option>
              ))}
            </select>
          );
        }
        return (
          <input
            type="text"
            value={formValues[f.key] ?? ''}
            onChange={(e) => handleFieldChange(f.key, e.target.value)}
            disabled={!canConfirm}
          />
        );
      case 'date':
        return (
          <input
            type="date"
            value={formValues[f.key] ?? ''}
            onChange={(e) => handleFieldChange(f.key, e.target.value)}
            disabled={!canConfirm}
          />
        );
      case 'number':
        return (
          <input
            type="text"
            value={formValues[f.key] ?? ''}
            onChange={(e) => handleFieldChange(f.key, e.target.value)}
            inputMode="decimal"
            disabled={!canConfirm}
          />
        );
      default:
        return (
          <input
            type="text"
            value={formValues[f.key] ?? ''}
            onChange={(e) => handleFieldChange(f.key, e.target.value)}
            disabled={!canConfirm}
          />
        );
    }
  };

  // ── Trade summary line ──
  const translatedProduct = t(`commodities.enums.${(trade.ProductType || '').toLowerCase()}`, trade.ProductType || '');
  const translatedDirection = t(`commodities.enums.${(trade.Direction || '').toLowerCase()}`, trade.Direction || '');

  return (
    <div className="commodity-mod-detail-panel">
      {/* Header */}
      <div className="commodity-mod-detail-header">
        <div>
          <div className="commodity-mod-detail-trade-num">
            {t('commodities.modifications.detailTitle', { tradeNumber: trade.TradeNumber })}
          </div>
          <div className="commodity-mod-detail-status">
            <span className="commodity-mod-detail-badge pending">
              <Clock size={12} />
              {isModify
                ? t('commodities.modifications.actionModify')
                : t('commodities.modifications.actionCancel')}
            </span>
          </div>
        </div>
        <button className="commodity-mod-detail-close" onClick={onClose}>
          <X size={16} />
        </button>
      </div>

      {/* Trade summary */}
      <table className="commodity-mod-detail-table">
        <tbody>
          <tr>
            <td className="detail-label">{fieldLabel('ProductType')}</td>
            <td>{translatedProduct || '—'}</td>
          </tr>
          <tr>
            <td className="detail-label">{fieldLabel('UnderlyingName')}</td>
            <td>{trade.UnderlyingName || '—'}</td>
          </tr>
          {trade.LegacyReference1 && (
            <tr>
              <td className="detail-label">{t('commodities.positions.grid.legacyReference1')}</td>
              <td>{trade.LegacyReference1}</td>
            </tr>
          )}
          <tr>
            <td className="detail-label">{fieldLabel('CounterpartyName')}</td>
            <td>{trade.CounterpartyName || '—'}</td>
          </tr>
          <tr>
            <td className="detail-label">{t('commodities.positions.grid.direction')}</td>
            <td>{translatedDirection || '—'}</td>
          </tr>
          <tr>
            <td className="detail-label">{fieldLabel('Lots')}</td>
            <td>{trade.Lots ?? '—'}</td>
          </tr>
          <tr>
            <td className="detail-label">{fieldLabel('Price')}</td>
            <td>{formatNumber(trade.Price)}</td>
          </tr>
          <tr>
            <td className="detail-label">{fieldLabel('Quantity')}</td>
            <td>{formatNumber(trade.Quantity)}</td>
          </tr>
        </tbody>
      </table>

      <div className="commodity-mod-detail-divider" />

      {/* Initiator info */}
      <table className="commodity-mod-detail-table">
        <tbody>
          <tr>
            <td className="detail-label">{t('commodities.modifications.detailInitiatedBy')}</td>
            <td>
              {pendingRequest.initiatedByName || '—'}
              {pendingRequest.initiatedAt ? ` — ${formatTimestamp(pendingRequest.initiatedAt)}` : ''}
            </td>
          </tr>
          {pendingRequest.comment && (
            <tr>
              <td className="detail-label">{t('commodities.modifications.commentLabel')}</td>
              <td className="detail-comment">{pendingRequest.comment}</td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="commodity-mod-detail-divider" />

      {/* ── Modify: editable fields table ── */}
      {isModify && (
        <>
          <div className="commodity-mod-detail-section-label">
            {t('commodities.modifications.pendingModifyFields')}
          </div>
          <table className="commodity-mod-fields-table commodity-mod-panel-fields" ref={tableRef}>
            <thead>
              <tr>
                <th>{''}</th>
                <th>{t('commodities.modifications.currentValue')}</th>
                <th>{t('commodities.modifications.newValue')}</th>
              </tr>
            </thead>
            <tbody>
              {DIALOG_FIELDS.map((f) => {
                if (f.optionOnly && !isOption) return null;

                const isChanged = f.key in changedFields;

                if (f.type === 'display') {
                  return (
                    <tr key={f.key} className="field-display-only">
                      <td className="field-label">{fieldLabel(f.key)}</td>
                      <td className="current-value" colSpan={2}>{trade[f.key] || '—'}</td>
                    </tr>
                  );
                }

                if (f.type === 'readonly') {
                  const showPreview = priceOrLotsChanged && previewQuantity != null;
                  return (
                    <tr key={f.key} className={showPreview ? 'field-changed' : ''}>
                      <td className="field-label">{fieldLabel(f.key)}</td>
                      <td className="current-value">{formatValue(f.key, trade[f.key])}</td>
                      <td className={showPreview ? 'calculated-value' : ''}>
                        {showPreview ? formatNumber(previewQuantity) : formatValue(f.key, trade[f.key])}
                      </td>
                    </tr>
                  );
                }

                const hasFieldError = fieldErrors.has(f.key);

                return (
                  <React.Fragment key={f.key}>
                    <tr data-field={f.key} className={`${isChanged ? 'field-changed' : ''}${hasFieldError ? ' field-error-row' : ''}`}>
                      <td className="field-label">{fieldLabel(f.key)}</td>
                      <td className="current-value">{formatValue(f.key, trade[f.key])}</td>
                      <td className="new-value">
                        {renderFieldInput(f)}
                      </td>
                    </tr>
                    {f.key === 'FixingReference' && maturityMonthChanged && (
                      <tr className="field-hint-row">
                        <td colSpan={3} className="field-hint">
                          <AlertTriangle size={13} />
                          {t('commodities.modifications.maturityMonthWarning')}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      {/* ── Cancel: reason + checkbox ── */}
      {isCancel && (
        <>
          <div className="commodity-mod-warning danger">
            <AlertTriangle size={16} />
            {t('commodities.modifications.permanentWarning')}
          </div>

          <div className="commodity-mod-comment">
            <label>{t('commodities.modifications.cancellationReasonLabel')} *</label>
            <textarea
              value={cancellationReason}
              onChange={(e) => { setCancellationReason(e.target.value); setError(null); }}
              placeholder={t('commodities.modifications.cancellationReasonPlaceholder')}
              rows={3}
              disabled={!canConfirm}
            />
          </div>

          <label className="commodity-mod-checkbox">
            <input
              type="checkbox"
              checked={cancelConfirmed}
              onChange={(e) => setCancelConfirmed(e.target.checked)}
              disabled={!canConfirm}
            />
            {t('commodities.modifications.confirmCheckbox')}
          </label>
        </>
      )}

      {/* Error message */}
      {error && (
        <div className="commodity-mod-panel-error">{error}</div>
      )}

      {/* ── Footer: Reject / Confirm buttons ── */}
      {canConfirm && !rejectMode && (
        <div className="commodity-mod-panel-footer">
          <button
            className="btn-secondary"
            onClick={() => setRejectMode(true)}
            disabled={submitting}
          >
            {t('commodities.modifications.rejectRequest')}
          </button>
          <button
            className="btn-primary"
            onClick={handleConfirm}
            disabled={submitting || (isModify && !hasChanges) || (isCancel && (!cancelConfirmed || !cancellationReason.trim()))}
          >
            {submitting
              ? t('commodities.modifications.submitting')
              : isModify
                ? t('commodities.modifications.confirmModification')
                : t('commodities.modifications.confirmCancellation')}
          </button>
        </div>
      )}

      {/* ── Reject mode: reason + confirm reject ── */}
      {canConfirm && rejectMode && (
        <div className="commodity-mod-panel-reject">
          <div className="commodity-mod-comment">
            <label>{t('commodities.modifications.rejectReasonLabel')} *</label>
            <textarea
              value={rejectReason}
              onChange={(e) => { setRejectReason(e.target.value); setError(null); }}
              placeholder={t('commodities.modifications.rejectReasonPlaceholder')}
              rows={3}
              autoFocus
            />
          </div>
          <div className="commodity-mod-panel-footer">
            <button
              className="btn-secondary"
              onClick={() => { setRejectMode(false); setRejectReason(''); setError(null); }}
              disabled={submitting}
            >
              {t('commodities.modifications.cancel')}
            </button>
            <button
              className="btn-danger"
              onClick={handleReject}
              disabled={submitting || !rejectReason.trim()}
            >
              {submitting
                ? t('commodities.modifications.submitting')
                : t('commodities.modifications.pendingRejectConfirm')}
            </button>
          </div>
        </div>
      )}

      {/* Not authorized to confirm */}
      {!canConfirm && (
        <div className="commodity-mod-panel-no-auth">
          {t('commodities.modifications.pendingCannotConfirm')}
        </div>
      )}
    </div>
  );
};

export default CommodityPendingDetailPanel;
